import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  ApolloError,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { getUserFacingErrorMessage } from './GraphQLAppError';
import { GraphQLResult, getError, getTypeOrNull } from './apollo';
import {
  ListCreateMutationCacheConfig,
  resolveCreateMutationOptions,
} from './apollo/listCache';
import { useMutationSignal } from './mutationSignal';
import { extractSignalFields } from './mutationSignalFields';

// Options that can be passed when using the hook
export type StandardMutationHookOptions<
  TResult,
  TMutation = unknown,
  TVariables = unknown,
> = {
  onComplete?: (result: TResult) => void;
  onError?: 'post-alert' | ((error: Error | ApolloError) => void);
  update?: MutationHookOptions<TMutation, TVariables>['update'];
  optimisticResponse?: MutationHookOptions<
    TMutation,
    TVariables
  >['optimisticResponse'];
};

// The result type for the mutation hook
export type StandardMutationHookResult<
  TName extends string,
  TResult,
  TArgs extends unknown[] = unknown[],
> = {
  [K in TName]: (...args: TArgs) => Promise<TResult>;
} & {
  error: Error | ApolloError | null;
  isMutating: boolean;
  called: boolean;
  result?: TResult;
};

// The type for the mutation result
export type MutationResult =
  | {
      __typename?: string;
    }
  | {
      __typename: 'Error';
      message?: string | null;
      code: string;
    }
  | null
  | undefined;

export function withStandardMutationHook<
  TMutation extends Partial<Record<TField, MutationResult>>,
  TResult,
  TVariables,
  TField extends string,
  TArgs extends unknown[] = [TVariables],
  TListItem extends Record<string, unknown> = Record<string, unknown>,
  TListScope = unknown,
>(
  useMutation: (
    options?: MutationHookOptions<TMutation, TVariables>,
  ) => MutationTuple<TMutation, TVariables>,
  mutationField: TField,
  transformInput: (...args: TArgs) => TVariables = ((variables: TVariables) =>
    variables) as unknown as (...args: TArgs) => TVariables,
  mutationOptions?: Pick<
    MutationHookOptions<TMutation, TVariables>,
    'awaitRefetchQueries' | 'refetchQueries' | 'update' | 'optimisticResponse'
  > & {
    cacheUpdate?: ListCreateMutationCacheConfig<
      TListItem,
      TListScope,
      TMutation,
      TVariables
    >;
  },
): (
  options?: StandardMutationHookOptions<TResult, TMutation, TVariables>,
) => StandardMutationHookResult<TField, TResult, TArgs> {
  const resolvedMutationOptions = resolveCreateMutationOptions<
    TMutation,
    TVariables,
    TListItem,
    TListScope
  >(mutationOptions);

  return (
    options: StandardMutationHookOptions<TResult, TMutation, TVariables> = {},
  ) => {
    const alert = useApi(alertApiRef);
    const emitSignal = useMutationSignal();
    const [mutate, { loading, error: apolloError, called, data }] = useMutation(
      resolvedMutationOptions,
    );

    const result = getTypeOrNull(
      data?.[mutationField] as GraphQLResult | null | undefined,
    );

    const error = useMemo(() => {
      if (apolloError) {
        return apolloError;
      }

      if (data) {
        const resultError = getError(
          data[mutationField] as GraphQLResult | null | undefined,
        );

        return resultError;
      }

      return null;
    }, [apolloError, data]);

    // we use a ref to avoid that the onError is called on every render
    // if its not stable.
    const onErrorValue = useRef(options.onError);
    useLayoutEffect(() => {
      onErrorValue.current = options.onError;
    }, [options.onError]);

    useEffect(() => {
      if (error && onErrorValue.current) {
        if (onErrorValue.current === 'post-alert') {
          alert.post({
            severity: 'error',
            message: getUserFacingErrorMessage(error, error.message),
          });
        } else {
          onErrorValue.current(error);
        }
      }
    }, [error, alert, onErrorValue]);

    // we use a ref to avoid that the onComplete is called on every render
    // if its not stable.
    const onCompleteValue = useRef(options.onComplete);
    useLayoutEffect(() => {
      onCompleteValue.current = options.onComplete;
    }, [options.onComplete]);

    useEffect(() => {
      if (result && onCompleteValue.current) {
        onCompleteValue.current(result);
      }
    }, [result]);

    // we use a ref to avoid recreating the mutation callback when update changes
    const updateRef = useRef(options.update);
    useLayoutEffect(() => {
      updateRef.current = options.update;
    }, [options.update]);

    // we use a ref to avoid recreating the mutation callback when optimisticResponse changes
    const optimisticResponseRef = useRef(options.optimisticResponse);
    useLayoutEffect(() => {
      optimisticResponseRef.current = options.optimisticResponse;
    }, [options.optimisticResponse]);

    const mutateItem = useCallback(
      async (...args: TArgs): Promise<TResult> => {
        const variables = transformInput(...args);
        const response = await mutate({
          variables,
          update: updateRef.current,
          optimisticResponse: optimisticResponseRef.current,
        });

        const resultError = getError(
          response.data?.[mutationField] as GraphQLResult | null | undefined,
        );
        if (resultError) {
          throw resultError;
        }

        const callResult: TResult | null = getTypeOrNull(
          response.data?.[mutationField] as GraphQLResult | null | undefined,
        );
        if (!callResult) {
          throw new Error('Mutation result is null');
        }

        const event = extractSignalFields(
          'update',
          callResult as Record<string, unknown> | null,
        );
        if (event) emitSignal(event);
        return callResult as TResult;
      },
      [mutate, emitSignal],
    );

    return {
      [mutationField]: mutateItem,
      error,
      isMutating: loading,
      called,
      result,
    } as StandardMutationHookResult<TField, TResult, TArgs>;
  };
}
